// Imported module by module rather than through the '@/utils' barrel: the
// barrel pulls in every helper in the folder, which drags unrelated libraries
// into anything that touches a table.
import { FiltersToFormData } from '@/utils/FiltersToFormData';
import { transformUuidToId } from '@/utils/transformUuidToId';
import { goTo403 } from "@/utils/Navigation";
import { getTenantBaseUrl } from "@/utils/tenant";

/**
 * Rows per page.
 *
 * 25 is the default for every list. The choice is kept in localStorage so it
 * survives into the next session — one setting shared by all tables, which is
 * how people described wanting it ("set it once, stop resetting it").
 *
 * There is deliberately no "All" option: these tables are server-side, and
 * asking for every row is what made large lists lock up the browser.
 */
const DEFAULT_PAGE_LENGTH = 25;
const PAGE_LENGTH_OPTIONS = [10, 25, 50, 100];
const PAGE_LENGTH_STORAGE_KEY = 'datatable:page-length';

const readStoredPageLength = () => {
    try {
        const stored = Number(window.localStorage.getItem(PAGE_LENGTH_STORAGE_KEY));

        return PAGE_LENGTH_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_LENGTH;
    } catch {
        // Private browsing and blocked storage both throw — fall back quietly.
        return DEFAULT_PAGE_LENGTH;
    }
};

const storePageLength = (length) => {
    if (!PAGE_LENGTH_OPTIONS.includes(Number(length))) return;

    try {
        window.localStorage.setItem(PAGE_LENGTH_STORAGE_KEY, String(length));
    } catch {
        // Nothing to do — the table still works, the choice just will not stick.
    }
};

const baseDataTableOptions = {
    processing: true,
    serverSide: true,
    scrollCollapse: false,
    responsive: false,
    autoWidth: false,
    scrollX: true,
    fixedHeader: {
        header: true,
        headerOffset: 76
    },
    colReorder: {
        enable: false,
    },
    columnDefs: [
        { targets: "_all", width: null }
    ],
    order: [[1, 'asc']],
    lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_OPTIONS.map(String)],
    drawCallback: function () {
        const api = this.api();
        api.columns.adjust();
    },
};

/**
 * Callers often pass their own `drawCallback` (to mount React into cells) or
 * `initComplete` (to hide columns). Those used to replace the base ones
 * outright, which quietly dropped the column-width adjust. Both are composed
 * instead: the base runs first, then the caller's.
 */
const createDataTableOptions = (customOptions = {}) => {
    const { drawCallback, initComplete, ...rest } = customOptions;

    return {
        ...baseDataTableOptions,
        pageLength: readStoredPageLength(),
        ...rest, // a page can still pin its own pageLength if it needs to
        drawCallback: function (settings) {
            baseDataTableOptions.drawCallback.call(this, settings);
            storePageLength(this.api().page.len());
            drawCallback?.call(this, settings);
        },
        initComplete: function (settings, json) {
            initComplete?.call(this, settings, json);
        },
    };
};

const createDataTableConfig = (endpoint, filterRef, token, logout, customDataSrc = null, method = 'POST') => {
    const SERVER_IP = getTenantBaseUrl();

    return {
        method: method,
        headers: {
            Authorization: `Bearer ${token}`,
        },
        url: `${SERVER_IP}${endpoint}`,
        xhrFields: { withCredentials: true },
        data(dt) {
            const activeFilters = filterRef.current;
            FiltersToFormData(activeFilters, dt);
            // if (requestCompanyValue && !dt.company_uuid) {
            //     dt.company_uuid = requestCompanyValue;
            // }
            // console.log('Final data sent to DataTable:', dt);
            return dt;
        },
        error: function (xhr, error, thrown) {
            if (xhr.status === 401) {
                logout();
            } else if (xhr.status === 403) {
                goTo403();
            }
        },
        dataSrc: function (json) {
            // console.log(" Raw response from API:", json);
            const transformed = transformUuidToId(json);
            // console.log("Transformed response (uuid → id):", transformed);

            if (customDataSrc) {
                customDataSrc(transformed);
            }
            return transformed.data || transformed; // DataTables expects array or object
        }
    };
};


export {
    createDataTableOptions,
    baseDataTableOptions,
    createDataTableConfig,
    readStoredPageLength,
    storePageLength,
    DEFAULT_PAGE_LENGTH,
    PAGE_LENGTH_OPTIONS,
    PAGE_LENGTH_STORAGE_KEY,
}
